/**
 * ARABIC registry of the dynamic excursion pages + FR↔AR slug join.
 *
 * AR content lives in slugs-ar/ (one file per excursion), typed `ExcursionPage`
 * exactly like FR → rendered by the shared ExcursionSlugView (locale="ar", RTL).
 * The FR structure stays the source (slug-excursions.ts); here we only add the
 * translation registry + the slug correspondence table.
 *
 * Slugs are proper-noun place names (Nice, Monaco, Verdon…) → mirrored from FR
 * one-to-one (the AR tree uses FR slugs), which keeps canonicals/hreflang stable
 * and avoids any collision.
 *
 * Consumed by: app/ar/excursions/[slug]/page.tsx (generateStaticParams) and the
 * metadata router. Mirrors excursions-en.ts.
 */
import type { ExcursionPage } from "./slug-excursions";
import niceMonacoAr from "./slugs-ar/nice-monaco";
import cannesGourdonAr from "./slugs-ar/cannes-gourdon";
import antibesJuanAr from "./slugs-ar/antibes-juan-les-pins";
import saintTropezAr from "./slugs-ar/saint-tropez";
import rivieraItalienneAr from "./slugs-ar/riviera-italienne";
import verdonAr from "./slugs-ar/verdon";
import ezeLaTurbieAr from "./slugs-ar/eze-la-turbie";
import saintPaulAr from "./slugs-ar/saint-paul-de-vence";

/** Stable FR↔AR join: the AR tree reuses the FR slugs one-to-one. */
export const EXCURSION_SLUG_MAP_AR: { fr: string; ar: string }[] = [
  { fr: "nice-monaco", ar: "nice-monaco" },
  { fr: "cannes-gourdon", ar: "cannes-gourdon" },
  { fr: "antibes-juan-les-pins", ar: "antibes-juan-les-pins" },
  { fr: "saint-tropez", ar: "saint-tropez" },
  { fr: "riviera-italienne", ar: "riviera-italienne" },
  { fr: "verdon", ar: "verdon" },
  { fr: "eze-la-turbie", ar: "eze-la-turbie" },
  { fr: "saint-paul-de-vence", ar: "saint-paul-de-vence" },
];

export const excursionPagesAr: ExcursionPage[] = [
  niceMonacoAr,
  cannesGourdonAr,
  antibesJuanAr,
  saintTropezAr,
  rivieraItalienneAr,
  verdonAr,
  ezeLaTurbieAr,
  saintPaulAr,
];

/** AR page by AR slug (= FR slug) or undefined. */
export const getExcursionPageAr = (arSlug: string): ExcursionPage | undefined =>
  excursionPagesAr.find((p) => p.slug === arSlug);

/** AR slugs (generateStaticParams + AR sitemap) — identical to the FR slugs. */
export const excursionArSlugs = (): string[] =>
  EXCURSION_SLUG_MAP_AR.map((m) => m.ar);

export const frToArExcursionSlug = (fr: string): string | undefined =>
  EXCURSION_SLUG_MAP_AR.find((m) => m.fr === fr)?.ar;

export const arToFrExcursionSlug = (ar: string): string | undefined =>
  EXCURSION_SLUG_MAP_AR.find((m) => m.ar === ar)?.fr;

/** AR canonical of an FR slug (the AR tree uses the FR slug). */
export const excursionArCanonical = (frSlug: string): string | undefined => {
  const ar = frToArExcursionSlug(frSlug);
  return ar ? `/ar/excursions/${ar}` : undefined;
};
